import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import type { Role } from '@ia/shared';
import { api, ApiError, errorMessage } from '../api';
import { useAuth } from '../auth';
import { Alert, Field, Spinner, When } from '../components/ui';

type Lookup = { email: string; role: Role; expiresAt: string };

export function AcceptInvitePage() {
  const { token = '' } = useParams();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState<Lookup | null>(null);
  const [lookupError, setLookupError] = useState<{ title: string; message: string } | null>(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .lookupInvite(token)
      .then((r) => alive && setInvite(r))
      .catch((e) => {
        if (!alive) return;
        if (e instanceof ApiError && e.status === 410)
          setLookupError({ title: 'Invite expired', message: 'This invite link has expired or was already used. Ask an admin to send a new one.' });
        else if (e instanceof ApiError && e.status === 404)
          setLookupError({ title: 'Invite not found', message: 'This invite link is not valid. It may have been revoked. Ask an admin to send a new one.' });
        else setLookupError({ title: 'Could not load invite', message: errorMessage(e) });
      });
    return () => {
      alive = false;
    };
  }, [token]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 12) return setError('Password must be at least 12 characters.');
    if (password !== confirm) return setError('Passwords do not match.');
    setBusy(true);
    try {
      const r = await api.acceptInvite({ token, name: name.trim(), password });
      setUser(r.user);
      navigate('/batches', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 410) setError('This invite has expired. Ask an admin to send a new one.');
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-screen">
      <div className="auth-card">
        {lookupError ? (
          <>
            <h1>{lookupError.title}</h1>
            <Alert kind="warn">{lookupError.message}</Alert>
            <Link to="/login">Go to sign in</Link>
          </>
        ) : !invite ? (
          <Spinner label="Checking invite…" />
        ) : (
          <form onSubmit={onSubmit}>
            <h1>Accept invite</h1>
            {user && (
              <Alert kind="info">
                You are currently signed in as {user.email}. Accepting will sign you in as the invited account instead.
              </Alert>
            )}
            <dl className="kv">
              <dt>Email</dt>
              <dd>{invite.email}</dd>
              <dt>Role</dt>
              <dd>
                <span className={`role role-${invite.role}`}>{invite.role}</span>
              </dd>
              <dt>Expires</dt>
              <dd>
                <When iso={invite.expiresAt} inline />
              </dd>
            </dl>
            <Field label="Your name">{(id) => <input id={id} value={name} onChange={(e) => setName(e.target.value)} required autoComplete="name" autoFocus />}</Field>
            <Field label="Password" hint="At least 12 characters.">
              {(id) => (
                <input id={id} type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={12} autoComplete="new-password" />
              )}
            </Field>
            <Field label="Confirm password">
              {(id) => (
                <input id={id} type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={12} autoComplete="new-password" />
              )}
            </Field>
            {error && <Alert>{error}</Alert>}
            <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
              {busy ? 'Creating account…' : 'Create account'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
